import {
  InventoryAnalyticsByDimensionAggregateBusinessInfo,
  InventoryAnalyticsByDimensionBaseBusinessInfo,
  InventoryAnalyticsDetailAggreageBusinessWithAlertInfoBusinessDataInner,
  InventoryAnalyticsDetailAggreageBusinessWithAlertInfoNumberData,
} from '@/coreInfrastructureModule/axios/generated/inventoryManage';
import { KpiFactory } from '../domains/factories/KpiFactory';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { DimensionValueGroup } from '../domains/entities/Dashboard';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { i18n } from '@/i18n';
import { DetailAlertBusinessData, DetailAlertData } from '../pages/useInventoryDashboardStore/types';
import { DetailAggregateBusinessDataFactory } from '../domains/factories/DetailAggregateBusinessDataFactory';

/**
 * ダッシュボードのアダプタ
 */
export class DashboardAdapter {
  /**
   * アラート詳細APIのレスポンスを画面表示用データに変換する。
   * @param bizInfo - 取引別のアラートデータ
   * @param numberInfo - 件数サマリ
   */
  public static adaptDetailAlertData(
    bizInfo: InventoryAnalyticsDetailAggreageBusinessWithAlertInfoBusinessDataInner[],
    numberInfo: InventoryAnalyticsDetailAggreageBusinessWithAlertInfoNumberData
  ): DetailAlertData {
    const businessList: DetailAlertBusinessData[] = bizInfo.map((b) => ({
      ...DetailAggregateBusinessDataFactory.create(b),
      alertType: b.alertType?.split(',') ?? [],
    }));
    const result: DetailAlertData = {
      overview: {
        excess: numberInfo.excess ?? 0,
        shortage: numberInfo.shortage ?? 0,
        unresolved: {
          unmatched: numberInfo.unmatched ?? 0,
          noOrderNumber: numberInfo.noOrderNumber ?? 0,
          deadline: numberInfo.deadline ?? 0,
        },
      },
      businesses: businessList,
    };
    return result;
  }
  /**
   * 集計取引の切り口別レスポンスを画面表示用に変換する
   * @param info - 集計取引の切り口別分析レスポンス
   * @returns 画面描画用の切り口グループ配列
   */
  public static adaptDimensionValueGroupsForAggBiz(info: InventoryAnalyticsByDimensionAggregateBusinessInfo): DimensionValueGroup[] {
    const collator = new Intl.Collator(i18n.locale, { numeric: true });
    const result: DimensionValueGroup[] = info.data
      .map((d) => ({
        dimensionValues: d.dimensionValues,
        kpi:
          d.kpi?.map((k) =>
            KpiFactory.create({
              kpiValue: Number(k.kpiValue),
              valid_from: DateService.convertUnixTimeToIDate(k.validFrom ? k.validFrom / 1000 : 0),
              valid_to: k.validTo ? DateService.convertUnixTimeToIDate(k.validTo / 1000) : undefined,
              isCurrent: k.isCurrent,
            })
          ) ?? [],
        businesses:
          d.businessData?.map((b) => ({
            ...BusinessFactory.create({
              ...b,
              businessId: b.aggregateBusinessId,
              aggregateBusinessResponsibleUsers:
                b.aggregateBusinessReponsibleUsers?.map((u) => UserAccountFactory.create({ email: u.id, name: u.name })) ?? undefined,
            }),
            endInventory: b.endInventoryAmount ? Number(b.endInventoryAmount) : undefined,
            salesResult: b.salesResultAmount ? Number(b.salesResultAmount) : undefined,
            customerForecast: b.customerForecastAmount ? Number(b.customerForecastAmount) : undefined,
            arrival: b.arrivalAmount ? Number(b.arrivalAmount) : undefined,
            supplierForecast: b.supplierForecastAmount ? Number(b.supplierForecastAmount) : undefined,
            orderAmount: b.orderAmount ? Number(b.orderAmount) : undefined,
            turnover: b.turnover ? Number(b.turnover) : undefined,
          })) ?? [],
        amounts:
          d.summaryData?.map((sd) => ({
            yearMonth: { year: sd.year, month: sd.month },
            arrival: sd.arrival ? Number(sd.arrival) : undefined,
            endInventory: sd.endInventory ? Number(sd.endInventory) : undefined,
            supplierForecast: sd.supplierForecast ? Number(sd.supplierForecast) : undefined,
            orderAmount: sd.orderAmount ? Number(sd.orderAmount) : undefined,
            turnover: sd.turnover ? Number(sd.turnover) : undefined,
            averageForecastAccuracy: sd.averageForecastAccuracy ? Number(sd.averageForecastAccuracy) : undefined,
            turnoverWithForecastAccuracy: sd.turnoverWithForecastAccuracy ? Number(sd.turnoverWithForecastAccuracy) : undefined,

            customerForecast: sd.customerForecast ? Number(sd.customerForecast) : undefined,
            forecastAccuracy: sd.forecastAccuracy ? Number(sd.forecastAccuracy) : undefined,
            salesResult: sd.salesResult ? Number(sd.salesResult) : undefined,
          })) ?? [],
      }))
      .sort((a, b) => {
        const aValueString = a.dimensionValues.name ?? `${a.dimensionValues.value ?? ''}${a.dimensionValues.unit ?? ''}`;
        const bValueString = b.dimensionValues.name ?? `${b.dimensionValues.value ?? ''}${b.dimensionValues.unit ?? ''}`;
        if (aValueString.trim() === '') return 1;
        if (bValueString.trim() === '') return -1;
        return collator.compare(aValueString, bValueString);
      });
    return result;
  }

  /**
   * 関連取引の切り口別レスポンスを画面表示用に変換する
   * @param info - 関連取引の切り口別分析レスポンス
   * @returns 画面描画用の切り口グループ配列
   */
  public static adaptDimensionValueGroupsForBaseBiz(info: InventoryAnalyticsByDimensionBaseBusinessInfo): DimensionValueGroup[] {
    const collator = new Intl.Collator(i18n.locale, { numeric: true });
    const result: DimensionValueGroup[] = info.data
      .map((d) => ({
        dimensionValues: d.dimensionValues,
        kpi:
          d.kpi?.map((k) =>
            KpiFactory.create({
              kpiValue: Number(k.kpiValue),
              valid_from: DateService.convertUnixTimeToIDate(k.validFrom ? k.validFrom / 1000 : 0),
              valid_to: k.validTo ? DateService.convertUnixTimeToIDate(k.validTo / 1000) : undefined,
              isCurrent: k.isCurrent,
            })
          ) ?? [],
        businesses:
          d.businessData?.map((b) => ({
            ...BusinessFactory.create({
              ...b,
              businessId: b.baseBusinessId,
              baseBusinessResponsibleUsers:
                b.baseBusinessResponsibleUsers?.map((u) => UserAccountFactory.create({ email: u.id, name: u.name })) ?? undefined,
            }),
            salesResult: b.salesResultAmount ? Number(b.salesResultAmount) : undefined,
            customerForecast: b.customerForecastAmount ? Number(b.customerForecastAmount) : undefined,
            forecastAccuracy: b.forecastAccuracy ? Number(b.forecastAccuracy) : undefined,
          })) ?? [],
        amounts:
          d.summaryData?.map((sd) => ({
            yearMonth: { year: sd.year, month: sd.month },
            customerForecast: sd.customerForecast ? Number(sd.customerForecast) : undefined,
            forecastAccuracy: sd.forecastAccuracy ? Number(sd.forecastAccuracy) : undefined,
            salesResult: sd.salesResult ? Number(sd.salesResult) : undefined,
          })) ?? [],
      }))
      .sort((a, b) => {
        const aValueString = a.dimensionValues.name ?? `${a.dimensionValues.value ?? ''}${a.dimensionValues.unit ?? ''}`;
        const bValueString = b.dimensionValues.name ?? `${b.dimensionValues.value ?? ''}${b.dimensionValues.unit ?? ''}`;
        if (aValueString.trim() === '') return 1;
        if (bValueString.trim() === '') return -1;
        return collator.compare(aValueString, bValueString);
      });
    return result;
  }
}
